import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { t } from '../../utils/i18n';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { getUsers, getUserDevices } from '../../api/authAdmin';
import { postPushTest } from '../../api/pushAdmin';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';
import TableBodySkeleton from '../../components/TableLoading/TableBodySkeleton';
import TableFetchOverlay from '../../components/TableLoading/TableFetchOverlay';

const PUSH_USER_SKELETON_WIDTHS = ['w-12', 'w-28', 'w-36', 'w-20', 'w-16 ml-auto'];

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const DEVICES_PAGE_SIZE = 20;

const DEFAULT_PUSH_BODY = {
  recipientId: 0,
  title: '테스트',
  subTitle: '',
  body: '푸시 테스트 메시지',
  data: { type: 'admin_test' },
  apns: { badge: 1, category: 'ADMIN_TEST' },
};

function defaultBodyJson() {
  return JSON.stringify(DEFAULT_PUSH_BODY, null, 2);
}

function MigrationChip({ needed }) {
  if (needed) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border bg-red-500/15 text-red-400 border-red-500/30">
        {t('push.migration.pending')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border bg-green-500/10 text-green-400 border-green-500/20">
      {t('push.migration.done')}
    </span>
  );
}

function mergeRecipientIntoBody(jsonText, recipientId) {
  const parsed = JSON.parse(jsonText);
  parsed.recipientId = Number(recipientId);
  return JSON.stringify(parsed, null, 2);
}

function validatePushPayload(payload) {
  const id = Number(payload?.recipientId);
  if (!Number.isFinite(id) || id < 1) {
    return t('push.validation.recipientId');
  }
  if (!String(payload?.title ?? '').trim()) {
    return t('push.validation.title');
  }
  if (!String(payload?.body ?? '').trim()) {
    return t('push.validation.body');
  }
  return null;
}

function mapPushError(err) {
  if (err?.code === -423 || err?.status === 404) {
    return t('push.errors.deviceNotFound');
  }
  if (err?.code === -4401 || err?.status === 403) {
    return t('push.errors.tokenExpired');
  }
  return err?.message || t('push.errors.generic');
}

export default function PushPage() {
  const navigate = useNavigate();
  const { role, token, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const isAdmin = role === 'admin';

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [bodyJson, setBodyJson] = useState(defaultBodyJson);
  const [jsonError, setJsonError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  const usersQuery = useQuery({
    queryKey: ['pushUsers', apiEnvironment, page, rowsPerPage, searchQuery],
    queryFn: () => getUsers(page, rowsPerPage, token, searchQuery),
    enabled: !!token,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setPage(1);
  }, [rowsPerPage, searchQuery]);

  const selectedUserId = selectedUser?.user_id ?? selectedUser?.UserID ?? null;

  const devicesQuery = useQuery({
    queryKey: ['pushUserDevices', apiEnvironment, selectedUserId],
    queryFn: () => getUserDevices(selectedUserId, 1, DEVICES_PAGE_SIZE, token),
    enabled: !!token && !!selectedUserId,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    const err = usersQuery.error || devicesQuery.error;
    if (err?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [usersQuery.error, devicesQuery.error, logout, navigate]);

  const {
    items: paginatedUsers = [],
    total_count: totalCount = 0,
    page: currentPage = 1,
    page_size: apiPageSize = rowsPerPage,
  } = usersQuery.data || {};
  const totalPages = Math.max(1, Math.ceil(totalCount / apiPageSize));

  const tableMinHeightPx = 56 + rowsPerPage * 48;

  const deviceItems = useMemo(
    () => devicesQuery.data?.items ?? devicesQuery.data?.data?.items ?? [],
    [devicesQuery.data],
  );
  const hasAnyPushToken = useMemo(
    () => deviceItems.some((d) => d.has_token ?? d.HasToken),
    [deviceItems],
  );

  const sendMutation = useMutation({
    mutationFn: (payload) => postPushTest(payload, token),
    onSuccess: (data) => {
      setSendResult(data);
      setSendError(null);
    },
    onError: (err) => {
      setSendError(err);
      setSendResult(null);
      if (err?.status === 401 || err?.code === -4301) {
        logout();
        navigate('/login', { replace: true });
      }
    },
  });

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchQuery(searchDraft.trim());
  };

  const handleSearchClear = () => {
    setSearchDraft('');
    setSearchQuery('');
    setPage(1);
  };

  const handleSelectUser = useCallback((user) => {
    const userId = user.user_id ?? user.UserID;
    setSelectedUser(user);
    setSendResult(null);
    setSendError(null);
    setValidationError('');
    setJsonError('');
    setBodyJson((prev) => {
      try {
        return mergeRecipientIntoBody(prev, userId);
      } catch {
        return JSON.stringify({ ...DEFAULT_PUSH_BODY, recipientId: Number(userId) }, null, 2);
      }
    });
  }, []);

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(bodyJson);
      setBodyJson(JSON.stringify(parsed, null, 2));
      setJsonError('');
    } catch (e) {
      setJsonError(t('push.body.parseError', { message: e.message }));
    }
  };

  const handleResetTemplate = () => {
    if (selectedUserId) {
      try {
        setBodyJson(mergeRecipientIntoBody(defaultBodyJson(), selectedUserId));
      } catch {
        setBodyJson(defaultBodyJson());
      }
    } else {
      setBodyJson(defaultBodyJson());
    }
    setJsonError('');
    setValidationError('');
  };

  const handleSend = () => {
    setValidationError('');
    setJsonError('');
    setSendResult(null);
    setSendError(null);

    let payload;
    try {
      payload = JSON.parse(bodyJson);
    } catch (e) {
      setJsonError(t('push.body.parseError', { message: e.message }));
      return;
    }

    const validationMsg = validatePushPayload(payload);
    if (validationMsg) {
      setValidationError(validationMsg);
      return;
    }

    sendMutation.mutate(payload);
  };

  const blockNoPushTarget =
    selectedUserId &&
    !devicesQuery.isLoading &&
    (deviceItems.length === 0 || !hasAnyPushToken);

  const canSend =
    isAdmin &&
    !sendMutation.isPending &&
    !jsonError &&
    !validationError &&
    !blockNoPushTarget;

  const usersShowFetchOverlay = usersQuery.isFetching && !usersQuery.isPending;
  const listError = usersQuery.isError ? usersQuery.error?.message : null;

  const PUSH_DEVICE_SKELETON_WIDTHS = ['w-20', 'w-24', 'w-16'];

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg">
      <div className="w-full max-w-[min(100%,1200px)] mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-text flex items-center gap-2">
            {t('push.title')}
            {isAdmin && (
              <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/40">
                Admin
              </span>
            )}
          </h1>
          <p className="text-dark-text-secondary text-sm mt-1">{t('push.description')}</p>
        </div>

        {listError && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {listError}
          </div>
        )}

        <form
          onSubmit={handleSearchSubmit}
          className="rounded-xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface/40 p-4 shadow-sm"
        >
          <label htmlFor="push-user-search" className="block text-xs font-medium tracking-wide text-dark-text-secondary uppercase">
            {t('push.search.label')}
          </label>
          <div className="mt-2 w-full max-w-lg">
            <div className="flex min-h-[2.75rem] w-full min-w-0 overflow-hidden rounded-lg border border-dark-border bg-dark-bg shadow-inner transition-colors focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/35">
              <input
                id="push-user-search"
                type="search"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder={t('push.search.placeholder')}
                className="min-w-0 flex-1 border-0 bg-transparent py-2.5 pl-4 pr-2 text-sm text-dark-text placeholder:text-dark-text-secondary/60 outline-none focus:ring-0"
                autoComplete="off"
              />
              <div className="flex shrink-0 divide-x divide-dark-border/80">
                <button
                  type="submit"
                  aria-label={t('push.search.submit')}
                  title={t('push.search.submit')}
                  className="flex items-center justify-center bg-primary/15 px-3.5 text-lg leading-none text-primary transition-colors hover:bg-primary/25"
                >
                  🔍
                </button>
                <button
                  type="button"
                  onClick={handleSearchClear}
                  aria-label={t('push.search.clear')}
                  title={t('push.search.clear')}
                  className="flex items-center justify-center bg-dark-surface/50 px-3.5 text-lg leading-none text-dark-text-secondary transition-colors hover:bg-dark-surface hover:text-primary"
                >
                  🗑️
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-dark-text-secondary/90">{t('push.search.hint')}</p>
          </div>
        </form>

        <PaginatedTableCard
          error={usersQuery.error}
          dataUpdatedAt={usersQuery.dataUpdatedAt}
          isPending={usersQuery.isPending}
          isFetching={usersQuery.isFetching}
          isEmpty={!usersQuery.isPending && paginatedUsers.length === 0}
          skeletonRows={rowsPerPage}
          skeletonCellWidths={PUSH_USER_SKELETON_WIDTHS}
          colSpan={5}
          containerStyle={{ minHeight: `${tableMinHeightPx}px` }}
          tableClassName="w-full min-w-[640px]"
          loadingLabel={t('push.table.loading')}
          emptyLabel={t('push.table.empty')}
          page={page}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setPage}
          prevLabel={t('push.pagination.prev')}
          nextLabel={t('push.pagination.next')}
          summary={t('push.pagination.page', { page: currentPage, totalPages })}
          pageSize={rowsPerPage}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={setRowsPerPage}
          pageSizeLabel={t('push.pagination.rowsPerPage')}
          header={(
            <tr className="border-b border-dark-border">
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('push.table.userId')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('push.table.username')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('push.table.name')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('push.table.needDeviceToken')}</th>
              <th className="text-right py-3 px-4 text-dark-text-secondary font-medium text-sm w-28" />
            </tr>
          )}
        >
          {paginatedUsers.map((user) => {
            const uid = user.user_id ?? user.UserID;
            const isSelected = selectedUserId != null && Number(selectedUserId) === Number(uid);
            return (
              <tr
                key={uid}
                className={`border-b border-dark-border last:border-0 hover:bg-dark-surface/50 ${isSelected ? 'bg-primary/5' : ''}`}
              >
                <td className="py-3 px-4 text-primary font-medium">{uid ?? '-'}</td>
                <td className="py-3 px-4 text-dark-text">{user.username ?? user.Username ?? '-'}</td>
                <td className="py-3 px-4 text-dark-text">{user.name ?? user.Name ?? '-'}</td>
                <td className="py-3 px-4">
                  <MigrationChip needed={user.need_device_token ?? user.NeedDeviceToken ?? false} />
                </td>
                <td className="py-3 px-4 text-right">
                  <button
                    type="button"
                    onClick={() => handleSelectUser(user)}
                    disabled={isSelected || usersShowFetchOverlay}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                      isSelected
                        ? 'border-primary/50 text-primary bg-primary/10 cursor-default'
                        : 'border-dark-border text-dark-text hover:border-primary hover:text-primary'
                    }`}
                  >
                    {isSelected ? t('push.table.selected') : t('push.table.select')}
                  </button>
                </td>
              </tr>
            );
          })}
        </PaginatedTableCard>

        <div className="bg-dark-card border border-dark-border rounded-lg p-4">
          <h2 className="text-sm font-semibold text-dark-text mb-2">{t('push.selected.title')}</h2>
          {selectedUser ? (
            <p className="text-sm text-dark-text-secondary">
              <span className="text-primary font-medium">{selectedUserId}</span>
              {' · '}
              {selectedUser.username ?? selectedUser.Username ?? '-'}
              {' · '}
              {selectedUser.name ?? selectedUser.Name ?? '-'}
            </p>
          ) : (
            <p className="text-sm text-dark-text-secondary">{t('push.selected.none')}</p>
          )}
        </div>

        {selectedUserId && (
          <div className="bg-dark-card border border-dark-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-dark-border">
              <h2 className="text-sm font-semibold text-dark-text">{t('push.devices.title')}</h2>
            </div>
            <div className="relative overflow-x-auto min-h-[10.5rem]">
              <table className="w-full min-w-[480px]">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('push.devices.platform')}</th>
                    <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('push.devices.appVersion')}</th>
                    <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('push.devices.hasToken')}</th>
                  </tr>
                </thead>
                <tbody>
                  {devicesQuery.isPending ? (
                    <TableBodySkeleton rows={3} cellWidths={PUSH_DEVICE_SKELETON_WIDTHS} />
                  ) : deviceItems.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-10 text-center text-dark-text-secondary text-sm">
                        {t('push.devices.empty')}
                      </td>
                    </tr>
                  ) : (
                    deviceItems.map((device) => {
                      const hasToken = device.has_token ?? device.HasToken ?? false;
                      return (
                        <tr
                          key={device.id ?? device.device_id ?? device.DeviceID}
                          className={`border-b border-dark-border last:border-0 ${devicesQuery.isFetching && !devicesQuery.isPending ? 'opacity-60' : ''}`}
                        >
                          <td className="py-3 px-4 text-dark-text text-sm">{device.app_platform ?? device.AppPlatform ?? '-'}</td>
                          <td className="py-3 px-4 text-dark-text-secondary text-sm">{device.app_version ?? device.AppVersion ?? '-'}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                hasToken ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                              }`}
                            >
                              {hasToken ? t('push.devices.yes') : t('push.devices.no')}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              <TableFetchOverlay
                show={devicesQuery.isFetching && !devicesQuery.isPending}
                label={t('push.devices.loading')}
              />
            </div>
            {deviceItems.length > 0 && !hasAnyPushToken && !devicesQuery.isPending && (
              <p className="px-4 py-3 text-sm text-amber-400 border-t border-dark-border bg-amber-500/5">
                {t('push.devices.noTokenWarning')}
              </p>
            )}
          </div>
        )}

        <div className="bg-dark-card border border-dark-border rounded-lg p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-dark-text">{t('push.body.title')}</h2>
              <p className="text-xs text-dark-text-secondary mt-1">{t('push.body.hint')}</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleFormatJson}
                className="px-3 py-1.5 text-xs rounded border border-dark-border text-dark-text-secondary hover:border-primary hover:text-primary transition-colors"
              >
                {t('push.body.format')}
              </button>
              <button
                type="button"
                onClick={handleResetTemplate}
                className="px-3 py-1.5 text-xs rounded border border-dark-border text-dark-text-secondary hover:border-primary hover:text-primary transition-colors"
              >
                {t('push.body.reset')}
              </button>
            </div>
          </div>

          <textarea
            value={bodyJson}
            onChange={(e) => {
              setBodyJson(e.target.value);
              setJsonError('');
              setValidationError('');
            }}
            rows={16}
            spellCheck={false}
            className="w-full font-mono text-xs leading-relaxed rounded-lg border border-dark-border bg-dark-bg text-dark-text p-3 focus:border-primary/60 focus:ring-1 focus:ring-primary/35 outline-none"
          />

          {jsonError && (
            <p className="text-sm text-red-400">{jsonError}</p>
          )}
          {validationError && (
            <p className="text-sm text-red-400">{validationError}</p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="px-4 py-2 rounded-lg bg-primary text-black text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
            >
              {sendMutation.isPending ? t('common.loading') : t('push.send')}
            </button>
            {!isAdmin && (
              <p className="text-xs text-dark-text-secondary">{t('push.sendDisabled')}</p>
            )}
          </div>
        </div>

        {sendResult && (
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 space-y-2">
            <p className="text-sm font-semibold text-green-400">{t('push.result.success')}</p>
            <dl className="text-xs font-mono text-dark-text space-y-1">
              <div>
                <dt className="text-dark-text-secondary inline">{t('push.result.notificationId')}: </dt>
                <dd className="inline break-all">{sendResult.notificationId ?? '-'}</dd>
              </div>
              <div>
                <dt className="text-dark-text-secondary inline">{t('push.result.messageId')}: </dt>
                <dd className="inline break-all">{sendResult.messageId ?? '-'}</dd>
              </div>
              <div>
                <dt className="text-dark-text-secondary inline">{t('push.result.endpointArn')}: </dt>
                <dd className="inline break-all">{sendResult.endpointArn ?? '-'}</dd>
              </div>
            </dl>
          </div>
        )}

        {sendError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
            <p className="text-sm font-semibold text-red-400">{t('push.result.error')}</p>
            <p className="text-sm text-red-300/90 mt-1">{mapPushError(sendError)}</p>
            {sendError.code != null && (
              <p className="text-xs text-dark-text-secondary mt-2 font-mono">
                HTTP {sendError.status} · code {sendError.code}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
